// App-wide route prefetching (the quicklink / instant.page pattern).
//
// Routes are code-split, so each chunk only downloads when the user clicks a
// link — every navigation pays a network round-trip first. This warms the
// matched route's lazy chunk as soon as the user *hovers or focuses* a local
// link (typically 100-300 ms before the click), so navigation feels instant.
// It triggers the exact same `() => import(...)` loaders Vue Router would run
// on navigation, just earlier, so there's no behavioural change — only timing.
//
// Safeguards: hover-capable devices only (no wasted data on touch taps that
// are already committing to the nav); same-origin app routes only; each route
// prefetched at most once; respects the Save-Data header / slow connections;
// all work is wrapped in requestIdleCallback and errors are swallowed.

const prefetched = new Set();

function canPrefetch() {
  const c = navigator.connection;
  if (c) {
    if (c.saveData) return false;
    if (typeof c.effectiveType === "string" && /(^|-)2g$/.test(c.effectiveType))
      return false;
  }
  return true;
}

export function useRoutePrefetch(router) {
  // Only attach on devices that actually hover. On touch, pointerover fires on
  // tap — too late to help and it would burn mobile data on every tap.
  const hoverCapable =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover)").matches;
  if (!hoverCapable || !canPrefetch()) {
    return { install() {}, uninstall() {} };
  }

  function warm(href) {
    let resolved;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return; // external link
      resolved = router.resolve(url.pathname + url.search);
    } catch {
      return;
    }
    if (!resolved || !resolved.matched.length) return;
    const key = resolved.fullPath;
    if (prefetched.has(key)) return;
    prefetched.add(key);
    // Fire the lazy loaders immediately — the user is hovering, so they're
    // likely about to click. The dynamic import is itself async/non-blocking,
    // so this doesn't stall the hover; it just races the chunk download ahead
    // of the click.
    for (const record of resolved.matched) {
      const comp = record.components && record.components.default;
      // Lazy routes store the `() => import(...)` loader as a function; an
      // already-resolved route stores the component object — skip those.
      if (typeof comp === "function") {
        try {
          Promise.resolve(comp()).catch(() => {});
        } catch {
          /* swallow — prefetch is best-effort */
        }
      }
    }
  }

  function onPointerOver(event) {
    const a =
      event.target && event.target.closest && event.target.closest("a[href]");
    if (a) warm(a.getAttribute("href"));
  }
  function onFocusIn(event) {
    const a =
      event.target && event.target.closest && event.target.closest("a[href]");
    if (a) warm(a.getAttribute("href"));
  }

  return {
    install() {
      document.addEventListener("pointerover", onPointerOver, {
        passive: true,
        capture: true,
      });
      document.addEventListener("focusin", onFocusIn, {
        passive: true,
        capture: true,
      });
    },
    uninstall() {
      document.removeEventListener("pointerover", onPointerOver, {
        capture: true,
      });
      document.removeEventListener("focusin", onFocusIn, { capture: true });
    },
  };
}
